/**
 * AboutPage — "Our Story" page for Anabiya Decor. Editorial layout.
 * All copy is overridable via the homepage editor's `about_story` section.
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Crown, Building2, Globe, Users, Target } from "lucide-react";
import { NavbarLuxe } from "@/components/NavbarLuxe";
import { AnnouncementBar } from "@/components/AnnouncementBar";
import { Footer } from "@/components/Footer";
import { FloatingButtons } from "@/components/FloatingButtons";
import { Seo } from "@/components/Seo";
import { useHomeContent } from "@/context/HomeContentContext";
import { useSmoothScroll } from "@/hooks/useSmoothScroll";

const DEFAULT_STORY = {
  kicker: "§ Our Story",
  headline: "A Legacy of Craftsmanship",
  headline_accent: " and Quality.",
  p1: "Anabiya Decor was founded in 2016 in the heart of Mumbai with a simple vision — to create premium home textiles that blend timeless elegance, superior quality and exceptional comfort.",
  p2: "Today, our collections are trusted by hotels, resorts, retailers and interior designers across 30+ countries. From bespoke bedding for luxury hospitality to signature pieces for design-led homes, every Anabiya piece is crafted with care and shipped worldwide.",
  p3: "Every piece we create reflects our passion for detail, fine fabrics and responsible manufacturing.",
  image: "https://images.unsplash.com/photo-1615874959474-d609969a20ed?auto=format&fit=crop&w=1400&q=80",
  cta_text: "Explore the collection",
  cta_href: "/shop",
  building_image: "https://images.unsplash.com/photo-1494526585095-c41746248156?auto=format&fit=crop&w=1400&q=80",
  values_title: "Where Tradition Meets Innovation",
  values: [
    { title: "Experienced Team", body: "Skilled artisans and craftsmanship professionals with decades of textile heritage.", icon: "Users" },
    { title: "Advanced Manufacturing", body: "Modern facilities with strict quality control and premium fabric sourcing.", icon: "Building2" },
    { title: "Global Standards", body: "International quality certifications and export compliance across 30+ countries.", icon: "Globe" },
    { title: "Client Focused", body: "Your success is our priority — flexible MOQ, custom sizes, private labelling.", icon: "Target" },
  ],
  quality_points: [
    { title: "Premium Quality", body: "Finest fabrics & superior craftsmanship" },
    { title: "Custom Made Solutions", body: "Bespoke sizes, designs & private labeling" },
    { title: "Wholesale Pricing", body: "Competitive factory-direct prices" },
    { title: "Worldwide Shipping", body: "Reliable & timely delivery" },
    { title: "Sustainable Practices", body: "Ethical production & sustainable future" },
  ],
  stats: [
    { number: "10+", label: "Years of excellence" },
    { number: "30+", label: "Countries served" },
    { number: "1000+", label: "Happy clients" },
    { number: "10000+", label: "Products delivered" },
  ],
};

const ICONS = { Users, Building2, Globe, Target, Crown };

const AboutPage = () => {
  useSmoothScroll();
  const { getSection } = useHomeContent();
  const s = getSection("about_story", DEFAULT_STORY);
  const values = Array.isArray(s.values) && s.values.length ? s.values : DEFAULT_STORY.values;
  const qp = Array.isArray(s.quality_points) && s.quality_points.length ? s.quality_points : DEFAULT_STORY.quality_points;
  const stats = Array.isArray(s.stats) && s.stats.length ? s.stats : DEFAULT_STORY.stats;

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <main data-testid="about-page" className="bg-white text-forest min-h-screen">
      <Seo
        path="/about"
        title="Our Story — A Legacy of Craftsmanship | Anabiya Decor"
        description="Founded in Mumbai in 2016, Anabiya Decor crafts premium home textiles for hotels, resorts and retailers across 30+ countries. Read our story of craftsmanship, quality and worldwide reach."
      />
      <AnnouncementBar />
      <NavbarLuxe />

      {/* Hero */}
      <section className="pt-24 md:pt-32 pb-14 md:pb-20 px-6 md:px-14 lg:px-20 border-b border-hairline">
        <div className="max-w-6xl mx-auto text-center">
          <div className="overline text-gold mb-4">{s.kicker || DEFAULT_STORY.kicker}</div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.7, 0, 0.2, 1] }}
            className="font-display text-forest text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-[-0.015em]"
          >
            {s.headline || DEFAULT_STORY.headline}
            <span className="font-editorial italic text-gold">{s.headline_accent || DEFAULT_STORY.headline_accent}</span>
          </motion.h1>
        </div>
      </section>

      {/* Two column story */}
      <section className="py-20 md:py-28 px-6 md:px-14 lg:px-20 bg-cream">
        <div className="max-w-6xl mx-auto grid grid-cols-12 gap-8 md:gap-14 items-center">
          <div className="col-span-12 md:col-span-6">
            <div className="aspect-[4/5] overflow-hidden bg-beige">
              <img
                src={s.image || DEFAULT_STORY.image}
                alt="Anabiya Decor — bedding craftsmanship story"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
          <div className="col-span-12 md:col-span-6">
            <div className="space-y-5 text-forest/80 text-base md:text-lg leading-relaxed font-light">
              <p data-testid="about-p1">{s.p1 || DEFAULT_STORY.p1}</p>
              <p data-testid="about-p2">{s.p2 || DEFAULT_STORY.p2}</p>
              <p data-testid="about-p3">{s.p3 || DEFAULT_STORY.p3}</p>
            </div>
            <Link
              to={s.cta_href || "/shop"}
              data-testid="about-cta"
              className="mt-10 inline-flex items-center gap-3 bg-forest text-champagne overline text-xs px-6 py-3.5 hover:bg-[hsl(150_60%_16%)] transition-colors"
            >
              {s.cta_text || DEFAULT_STORY.cta_text} <ArrowRight size={13} strokeWidth={1.6} />
            </Link>
          </div>
        </div>
      </section>

      {/* Quality highlights bar */}
      <section className="py-20 md:py-24 px-6 md:px-14 lg:px-20 bg-forest text-champagne">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-5 gap-6 md:gap-8">
          {qp.map((q, i) => (
            <div key={i} className="flex flex-col items-start" data-testid={`quality-point-${i}`}>
              <Crown size={22} strokeWidth={1.4} className="text-gold mb-4" />
              <div className="font-display text-champagne text-lg leading-tight">{q.title}</div>
              <div className="text-champagne/60 text-sm mt-2">{q.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Values grid */}
      <section className="py-20 md:py-28 px-6 md:px-14 lg:px-20 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="overline text-gold">§ About Our Company</div>
          <h2 className="font-display text-forest text-4xl md:text-6xl mt-3 leading-[1.05] tracking-tight max-w-3xl">
            {s.values_title || DEFAULT_STORY.values_title}
          </h2>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((v, i) => {
              const Icon = ICONS[v.icon] || Crown;
              return (
                <div key={i} className="flex flex-col items-start" data-testid={`value-${i}`}>
                  <Icon size={24} strokeWidth={1.4} className="text-gold mb-4" />
                  <div className="font-display text-forest text-xl leading-tight">{v.title}</div>
                  <div className="text-forest/70 text-sm mt-2 leading-relaxed">{v.body}</div>
                </div>
              );
            })}
          </div>

          {s.building_image && (
            <div className="mt-14 aspect-[16/6] overflow-hidden bg-beige">
              <img src={s.building_image} alt="Anabiya Decor manufacturing facility" className="w-full h-full object-cover" loading="lazy" />
            </div>
          )}
        </div>
      </section>

      {/* Additional editorial sections: Philosophy · Craftsmanship · SPNE · International Vision */}
      <section id="philosophy" data-testid="about-philosophy" className="py-20 md:py-24 px-6 md:px-14 lg:px-20 bg-white border-t border-hairline">
        <div className="max-w-4xl">
          <div className="overline text-gold tracking-[4px] text-[11px] mb-3">§ Our Philosophy</div>
          <h2 className="font-display text-forest text-4xl md:text-5xl leading-[1.05] tracking-tight">
            Considered pieces, made for<span className="italic font-editorial text-champagne"> beautiful everyday living.</span>
          </h2>
          <p className="mt-6 text-forest/70 text-base md:text-lg leading-relaxed font-light max-w-2xl">
            Our collections are built on honest materials, refined proportions and quiet detailing —
            timeless textiles designed to feel restful and restorative in modern spaces.
          </p>
        </div>
      </section>

      <section id="craftsmanship" data-testid="about-craftsmanship" className="py-20 md:py-24 px-6 md:px-14 lg:px-20 bg-cream border-t border-hairline">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="overline text-gold tracking-[4px] text-[11px] mb-3">§ Craftsmanship</div>
            <h2 className="font-display text-forest text-4xl md:text-5xl leading-[1.05] tracking-tight">
              Crafted in Mumbai, <span className="italic font-editorial text-champagne">with care.</span>
            </h2>
            <p className="mt-6 text-forest/70 text-base md:text-lg leading-relaxed font-light">
              Every Anabiya piece is made in our Mumbai facility with premium fabrics,
              refined finishing and careful quality control. Custom sizing, monogramming
              and private-label programmes are available on request.
            </p>
          </div>
          <div className="aspect-[4/3] overflow-hidden bg-beige">
            <img src="https://images.unsplash.com/photo-1587467512961-120760940315?auto=format&fit=crop&w=1200&q=80" alt="Anabiya Decor craftsmanship" loading="lazy" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      <section id="spne" data-testid="about-spne" className="py-20 md:py-24 px-6 md:px-14 lg:px-20 bg-white border-t border-hairline">
        <div className="max-w-4xl">
          <div className="overline text-gold tracking-[4px] text-[11px] mb-3">§ Anabiya Decor × SPNE Design</div>
          <h2 className="font-display text-forest text-4xl md:text-5xl leading-[1.05] tracking-tight">
            Where craftsmanship meets<span className="italic font-editorial text-champagne"> contemporary design.</span>
          </h2>
          <p className="mt-6 text-forest/70 text-base md:text-lg leading-relaxed font-light max-w-2xl">
            Our design collaboration with SPNE Design brings a thoughtful contemporary perspective
            to Anabiya's textile heritage — shaping considered collections for modern spaces worldwide.
          </p>
          <Link
            to="/spne-design"
            data-testid="about-spne-cta"
            className="mt-8 inline-flex items-center gap-3 bg-forest text-champagne overline text-xs px-8 py-4 hover:bg-[hsl(150_60%_16%)] transition-colors tracking-[3px]"
          >
            Discover SPNE Design <ArrowRight size={13} strokeWidth={1.8} />
          </Link>
        </div>
      </section>

      <section id="leadership" data-testid="about-leadership" className="py-20 md:py-24 px-6 md:px-14 lg:px-20 bg-cream border-t border-hairline">
        <div className="max-w-4xl">
          <div className="overline text-gold tracking-[4px] text-[11px] mb-3">§ Leadership & Design</div>
          <h2 className="font-display text-forest text-4xl md:text-5xl leading-[1.05] tracking-tight">
            MDSR<span className="italic font-editorial text-champagne"> — CEO & Designer.</span>
          </h2>
          <p className="mt-6 text-forest/70 text-base md:text-lg leading-relaxed font-light max-w-2xl">
            Anabiya Decor is guided by MDSR — leading design direction, product development and
            manufacturing operations across the studio's Mumbai workshops.
          </p>
        </div>
      </section>

      <section id="international" data-testid="about-international" className="py-20 md:py-24 px-6 md:px-14 lg:px-20 bg-forest text-champagne">
        <div className="max-w-4xl">
          <div className="overline text-gold tracking-[4px] text-[11px] mb-3">§ International Vision</div>
          <h2 className="font-display text-champagne text-4xl md:text-5xl leading-[1.05] tracking-tight">
            Made in India, <span className="italic font-editorial text-gold">for the world.</span>
          </h2>
          <p className="mt-6 text-champagne/70 text-base md:text-lg leading-relaxed font-light max-w-2xl">
            We ship worldwide from Mumbai to homes, hotels and design studios across the US, UK,
            Australia, UAE and Europe — with USD pricing, WhatsApp support and dedicated trade programmes.
          </p>
        </div>
      </section>

      {/* Stats + CTA */}
      <section className="py-20 md:py-24 px-6 md:px-14 lg:px-20 bg-cream border-t border-hairline">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center md:text-left">
          {stats.map((st, i) => (
            <div key={i} data-testid={`stat-${i}`}>
              <div className="font-display text-forest text-5xl md:text-6xl tabnum leading-none">{st.number}</div>
              <div className="overline text-forest/60 mt-2 text-[10px] tracking-[3px]">{st.label}</div>
            </div>
          ))}
        </div>
        <div className="max-w-6xl mx-auto mt-14 text-center">
          <Link
            to="/shop"
            className="inline-flex items-center gap-3 bg-forest text-champagne overline text-xs px-8 py-4 hover:bg-[hsl(150_60%_16%)] transition-colors"
            data-testid="about-shop-cta"
          >
            Explore the collection <ArrowRight size={13} strokeWidth={1.6} />
          </Link>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </main>
  );
};

export default AboutPage;
